import { useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { PASSWORD_MIN } from '@sp/shared'
import { useSession } from '../session.tsx'
import { api } from '../lib/api.ts'
import { HttpError } from '../lib/http.ts'
import { formatDay } from '../lib/format.ts'
import { errorMessage, useResource } from '../lib/useResource.ts'
import { AvatarStack, Button, Card, PageTitle, Paragraph, Spinner, TextCol, TextField } from '../components/ui.tsx'
import { Brand, PasswordField } from '../components/bits.tsx'

/**
 * Prijeti pozvanky (Dropbox / ChatGPT v navrhu). Funguje prihlasenemu
 * i neprihlasenemu — pozvany muze mit ucet z jine skupiny.
 */
export function InvitePage() {
  const { token = '' } = useParams<{ token: string }>()
  const session = useSession()
  const navigate = useNavigate()
  const preview = useResource(() => api.invites.preview(token), [token])
  const [mode, setMode] = useState<'register' | 'login'>('register')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const me = session.state.status === 'signed-in' ? session.state.me : null
  const data = preview.res.state === 'ok' ? preview.res.data : null

  async function run(action: () => Promise<void>) {
    setBusy(true)
    setError(null)
    try {
      await action()
    } catch (err) {
      // E-mail uz ucet ma: nabidnout prihlaseni a prijeti pozvanky.
      if (err instanceof HttpError && err.status === 409 && data?.groupName) setMode('login')
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  function finish(groupId: string | null) {
    if (groupId) session.selectGroup(groupId)
    navigate('/', { replace: true })
  }

  async function accept() {
    const res = await api.invites.accept(token)
    await session.refresh()
    finish(res.groupId)
  }

  function register(e: FormEvent) {
    e.preventDefault()
    void run(async () => finish(await session.register({ name: name.trim(), email, password, inviteToken: token })))
  }

  function loginAndAccept(e: FormEvent) {
    e.preventDefault()
    void run(async () => {
      await session.login(email, password)
      await accept()
    })
  }

  const switchMode = (next: 'register' | 'login') => {
    setMode(next)
    setError(null)
  }

  return (
    <div className="app-shell">
      <div className="page">
        <main className="page-content auth-page">
          <Brand />
          {preview.res.state === 'loading' && <Spinner />}

          {preview.res.state === 'error' && (
            <>
              <PageTitle size="hero" title={preview.offline ? 'Bez připojení' : 'Pozvánka už neplatí'} />
              <Paragraph>
                {preview.offline
                  ? 'Bez signálu se pozvánka nedá ověřit. Zkus odkaz otevřít znovu, až bude připojení.'
                  : 'Odkaz vypršel, někdo ho zrušil, nebo už byl použitý. Požádej o nový.'}
              </Paragraph>
              <Button className="self-start" onClick={() => navigate('/', { replace: true })}>
                {me ? 'Do aplikace' : 'Přihlásit se'}
              </Button>
            </>
          )}

          {data && (
            <>
              {data.memberNames.length > 0 && <AvatarStack names={data.memberNames} size={44} />}
              <PageTitle
                size="hero"
                title={
                  data.groupName
                    ? `${data.inviterName ?? 'Někdo'} tě zve do skupiny „${data.groupName}“`
                    : 'Založ si účet v Sériových nákupech'
                }
              />
              <Paragraph>
                {data.groupName
                  ? 'Společná šablona, obchody a nákupy. Co kdo přidá nebo odškrtne, uvidí ostatní hned.'
                  : 'Po založení účtu si vytvoříš skupinu a pozveš do ní ostatní.'}
              </Paragraph>

              {me ? (
                data.groupId && me.groups.some((g) => g.id === data.groupId) ? (
                  <Card>
                    <Paragraph tone="fg">Ve skupině „{data.groupName}“ už jsi.</Paragraph>
                    <Button kind="primary" full onClick={() => finish(data.groupId)}>
                      Otevřít skupinu
                    </Button>
                  </Card>
                ) : data.groupName ? (
                  <Card>
                    <TextCol title={me.user.name} sub={me.user.email} />
                    {error && <Paragraph tone="error">{error}</Paragraph>}
                    <Button kind="primary" full disabled={busy} onClick={() => void run(accept)}>
                      Přidat se ke skupině
                    </Button>
                  </Card>
                ) : (
                  <Card>
                    <Paragraph tone="fg">
                      Tahle pozvánka slouží k založení nového účtu. Teď jsi v aplikaci jako {me.user.name}.
                    </Paragraph>
                    <div className="button-row">
                      <Button onClick={() => navigate('/', { replace: true })}>Do aplikace</Button>
                      <Button kind="danger" onClick={() => void session.logout()}>
                        Odhlásit se
                      </Button>
                    </div>
                  </Card>
                )
              ) : mode === 'register' ? (
                <>
                  <Card>
                    <form className="stack" onSubmit={register}>
                      <TextField
                        label="Jméno"
                        placeholder="Jak ti mají ostatní říkat"
                        autoComplete="nickname"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        maxLength={60}
                        required
                      />
                      <TextField
                        label="E-mail"
                        placeholder="Budeš se jím přihlašovat"
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                      <PasswordField
                        label="Heslo"
                        placeholder={`Aspoň ${PASSWORD_MIN} znaků`}
                        autoComplete="new-password"
                        minLength={PASSWORD_MIN}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                      {error && <Paragraph tone="error">{error}</Paragraph>}
                      <Button kind="primary" type="submit" full disabled={busy}>
                        {data.groupName ? 'Přidat se ke skupině' : 'Založit účet'}
                      </Button>
                    </form>
                  </Card>
                  {data.groupName && (
                    <div className="center-row">
                      <span>Už máš účet?</span>
                      <Button kind="link" compact onClick={() => switchMode('login')}>
                        Přihlásit se
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <Card>
                    <form className="stack" onSubmit={loginAndAccept}>
                      <TextField
                        label="E-mail"
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                      <PasswordField
                        label="Heslo"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                      {error && <Paragraph tone="error">{error}</Paragraph>}
                      <Button kind="primary" type="submit" full disabled={busy}>
                        Přihlásit se a přidat se
                      </Button>
                    </form>
                  </Card>
                  <div className="center-row">
                    <span>Nemáš účet?</span>
                    <Button kind="link" compact onClick={() => switchMode('register')}>
                      Založit účet
                    </Button>
                  </div>
                </>
              )}

              <Paragraph size="meta" align="center">
                Pozvánka platí do {formatDay(data.expiresAt)}
              </Paragraph>
            </>
          )}
        </main>
      </div>
    </div>
  )
}
